/**
 * Notification Center Component
 * Displays a dropdown bell icon with recent notifications
 *
 * Features:
 * - Bell icon with unread badge count
 * - Dropdown notification list
 * - Mark notifications as read
 * - Delete notifications
 * - Click to navigate to relevant page
 * - Auto-update on new notifications
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import './NotificationCenter.css';

interface Notification {
  id: string;
  title: string;
  body: string;
  type: string;
  read: boolean;
  createdAt: Date;
  data?: Record<string, string>;
}

/**
 * NotificationCenter Component
 * Shows notifications bell and dropdown list
 */
export const NotificationCenter: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  // Subscribe to notifications
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(20)
      );

      const unsubscribe = onSnapshot(q, snapshot => {
        const notifs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
        } as Notification));

        setNotifications(notifs);
        setUnreadCount(notifs.filter(n => !n.read).length);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading notifications:', error);
      setLoading(false);
    }
  }, [userId]);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    markAsRead(notification.id);

    // Navigate if action is specified
    if (notification.data?.action) {
      const routes: Record<string, string> = {
        viewWallet: '/wallet',
        viewReports: '/reports',
        viewDashboard: '/dashboard',
        viewTransactions: '/transactions',
        viewProfile: '/profile',
      };

      const route = routes[notification.data.action];
      if (route) {
        window.location.href = route;
      }
    }

    setIsOpen(false);
  };

  const markAsRead = async (notificationId: string) => {
    try {
      // Could implement via Cloud Function if needed
      // For now, UI update is sufficient
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      // Could implement via Cloud Function
      // For now, UI update is sufficient
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return (
    <div className="notification-center">
      <button className="bell-button" onClick={handleBellClick} aria-label="Notifications">
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <span className="unread-indicator">{unreadCount} new</span>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="empty-state">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="empty-state">No notifications yet</div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-body">{notification.body}</div>
                    <div className="notification-time">
                      {formatTime(notification.createdAt)}
                    </div>
                  </div>

                  <button
                    className="delete-button"
                    onClick={e => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                    aria-label="Delete notification"
                  >
                    ✕
                  </button>

                  {!notification.read && (
                    <div className="unread-indicator-dot"></div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="notification-footer">
            <a href="/notifications" className="view-all-link">
              View All Notifications
            </a>
          </div>
        </div>
      )}

      {isOpen && <div className="overlay" onClick={() => setIsOpen(false)}></div>}
    </div>
  );
};

/**
 * Format time relative to now
 */
function formatTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return date.toLocaleDateString();
}

export default NotificationCenter;
